import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Phone, Pen, Mail, CheckCircle, Heart, Shield, Download } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import './HomePage.css';

import { setJsonLd, setPageSeo } from '../utils/seo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function HomePage() {
  const navigate = useNavigate();
  const [optinEmail, setOptinEmail] = useState('');
  const [optinName, setOptinName] = useState('');
  const [optinStatus, setOptinStatus] = useState(null);
  const [optinLoading, setOptinLoading] = useState(false);
  const { formatPrice, currency } = useCurrency();

  useEffect(() => {
    setPageSeo({
      title: 'Keepsake Letters from Loved Ones | Binding Thread',
      description: 'Binding Thread turns your loved one\u2019s spoken memories into beautifully crafted keepsake letter collections. Three carefully crafted collections from $149 \u2014 keepsake tin and shipping included. They call, we write. No technology needed.',
      canonical: 'https://www.bindingthread.com.au/',
      ogImage: 'https://www.bindingthread.com.au/logo-final.png'
    });
    setJsonLd('ld-org', {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Binding Thread",
      "url": "https://www.bindingthread.com.au",
      "logo": "https://www.bindingthread.com.au/logo-final.png",
      "description": "Binding Thread turns your loved one\u2019s spoken memories into beautifully crafted keepsake letter collections.",
      "email": "admin@bindingthread.com.au",
      "telephone": "+61412024059",
      "foundingDate": "2025",
      "founder": {
        "@type": "Person",
        "name": "Mary-Ann Roche"
      },
      "sameAs": [
        "https://www.facebook.com/BindingThread01/",
        "https://www.instagram.com/bindingthread01",
        "https://www.tiktok.com/@bindingthread"
      ]
    });
    setJsonLd('ld-website', {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Binding Thread",
      "url": "https://www.bindingthread.com.au"
    });
    setJsonLd('ld-service', {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Keepsake Letter Collections",
      "description": "A keepsake letter service that transforms your loved one\u2019s spoken memories into beautifully crafted keepsake letters on archival parchment paper. Choose from three collections: A Thread (3 letters), Woven (6 letters) or Tapestry (12 letters). Keepsake tin and shipping included.",
      "provider": {
        "@type": "Organization",
        "name": "Binding Thread",
        "url": "https://www.bindingthread.com.au"
      },
      "serviceType": "Memory Preservation Service",
      "areaServed": {
        "@type": "Country",
        "name": "Australia"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Keepsake Collections",
        "itemListElement": [
          { "@type": "Offer", "name": "A Thread", "price": "149", "priceCurrency": "AUD" },
          { "@type": "Offer", "name": "Woven", "price": "249", "priceCurrency": "AUD" },
          { "@type": "Offer", "name": "Tapestry", "price": "429", "priceCurrency": "AUD" }
        ]
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOptin = async (e) => {
    e.preventDefault();
    if (!optinEmail) return;
    setOptinLoading(true);
    try {
      await axios.post(`${API_URL}/api/leads/optin`, { email: optinEmail, name: optinName });
      setOptinStatus('success');
      const link = document.createElement('a');
      link.href = '/downloads/10-questions-guide.pdf';
      link.download = '10-Questions-to-Ask-Your-Mum-Binding-Thread.pdf';
      link.click();
      setTimeout(() => navigate('/thank-you'), 1000);
    } catch (error) {
      setOptinStatus('error');
    } finally {
      setOptinLoading(false);
    }
  };

  return (
    <div className="home-page" data-testid="home-page">
      {/* Pricing Banner */}
      <div className="early-bird-bar" data-testid="pricing-banner">
        <div className="early-bird-content">
          <span className="early-bird-line1">
            <strong>Keepsake letter collections from {currency === 'USD' ? '$99' : '$149'}</strong>
          </span>
          <span className="early-bird-line2">
            Keepsake tin &amp; shipping included in every collection
          </span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <img
          className="hero-bg-image"
          src="https://customer-assets.emergentagent.com/job_88756677-6aaa-483f-ad44-dd37922f08c5/artifacts/xzvqwrae_Image_20260410_202216.png"
          alt=""
          aria-hidden="true"
        />
        <div className="hero-container">
          <div className="hero-content hero-content-full">
            <h1 className="hero-title" data-testid="hero-title">
              Preserve Your Loved One&apos;s Memories as Beautifully Crafted Keepsake Letters
            </h1>
            <p className="hero-subtitle" data-testid="hero-subtitle">
              They simply call and share their stories. We beautifully craft them into keepsake letters and send them to you.
            </p>
            <p className="hero-subtitle-secondary" data-testid="hero-subtitle-secondary">
              No apps. No technology. Just meaningful memories preserved forever.
            </p>
            <Link to="/shop" className="btn btn-urgent hero-cta" data-testid="cta-purchase">
              Start Capturing Their Stories
            </Link>
            <div style={{ marginTop: '0.75rem' }}>
              <Link to="/how-it-works" className="hero-secondary-link">See how it works &rarr;</Link>
            </div>
            <div className="hero-social" data-testid="hero-social-links">
              <span className="hero-social-label">Follow our journey</span>
              <div className="hero-social-icons">
                <a href="https://www.facebook.com/BindingThread01/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="hero-social-facebook">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="https://www.instagram.com/bindingthread01" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="hero-social-instagram">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.17.054 1.805.249 2.228.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0-2.163C8.741 0 8.333.014 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.741 0 12s.014 3.668.072 4.948c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.741 24 12 24s3.668-.014 4.948-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.948s-.014-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.668.012 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.882 0 1.441 1.441 0 012.882 0z"/></svg>
                </a>
                <a href="https://www.tiktok.com/@bindingthread" target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="hero-social-tiktok">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="how-it-works section" data-testid="how-it-works">
        <div className="container">
          <div className="section-header text-center">
            <h2>How It Works</h2>
            <p className="section-description">
              Simple for them. Priceless for you.
            </p>
          </div>

          <div className="pillars">
            <div className="pillar" data-testid="pillar-call">
              <div className="pillar-image-wrapper">
                <img 
                  src="https://images.unsplash.com/photo-1601175864100-1b8332d228d1?w=500&q=80&fm=webp" 
                  alt="Smiling elderly man sharing a story over the phone"
                  className="pillar-image"
                  width="500"
                  height="333"
                />
              </div>
              <div className="pillar-content">
                <div className="pillar-icon">
                  <Phone size={24} />
                </div>
                <h3>The Call</h3>
                <p>
                  Your loved one calls our number whenever a memory comes to mind — morning, noon, or night. No technology, no pressure — just their voice.
                </p>
              </div>
            </div>

            <div className="pillar" data-testid="pillar-craft">
              <div className="pillar-image-wrapper">
                <img 
                  src="https://customer-assets.emergentagent.com/job_719e6e75-4da9-4456-82ce-19b3fc98bea1/artifacts/zsuhcq2s_margarita_kochneva-text-4095909_1920.jpg" 
                  alt="Stack of keepsake letters on parchment paper tied with twine"
                  className="pillar-image"
                  width="500"
                  height="333"
                />
              </div>
              <div className="pillar-content">
                <div className="pillar-icon">
                  <Pen size={24} />
                </div>
                <h3>The Craft</h3>
                <p>
                  We listen carefully and turn each recording into a beautifully crafted keepsake letter, preserving every detail.
                </p>
              </div>
            </div>

            <div className="pillar" data-testid="pillar-arrival">
              <div className="pillar-image-wrapper">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ab110b33-d84b-4603-b690-422196896d5d/artifacts/gzz4worw_woman%20collecting%20mail.jpg" 
                  alt="Woman opening her letterbox to find a keepsake letter"
                  className="pillar-image"
                  width="500"
                  height="333"
                />
              </div>
              <div className="pillar-content">
                <div className="pillar-icon">
                  <Mail size={24} />
                </div>
                <h3>The Arrival</h3>
                <p>
                  Each letter arrives at your door — a tangible piece of your family&apos;s story, ready to keep and share.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stories Families Treasure Most — emotional transition */}
      <section className="stories-treasured" data-testid="stories-treasured-section" aria-labelledby="stories-treasured-heading">
        <div className="container">
          <div className="stories-treasured-head">
            <h2 id="stories-treasured-heading">The Stories Families Treasure Most</h2>
            <p className="stories-treasured-sub">
              The smallest memories are often the ones families hold onto forever.
            </p>
          </div>

          <div className="stories-treasured-rail" role="list" aria-label="Memories families have captured">
            <article className="memory-card" role="listitem" data-testid="memory-card-1">
              <span className="memory-quote-mark" aria-hidden="true">&ldquo;</span>
              <p className="memory-text">
                Dad finally told us why he stopped playing football at seventeen.
              </p>
            </article>

            <article className="memory-card" role="listitem" data-testid="memory-card-2">
              <span className="memory-quote-mark" aria-hidden="true">&ldquo;</span>
              <p className="memory-text">
                Nan spoke about arriving in Australia with one suitcase and no idea what came next.
              </p>
            </article>

            <article className="memory-card" role="listitem" data-testid="memory-card-3">
              <span className="memory-quote-mark" aria-hidden="true">&ldquo;</span>
              <p className="memory-text">
                My grandfather described the exact moment he first saw the ocean.
              </p>
            </article>

            <article className="memory-card" role="listitem" data-testid="memory-card-4">
              <span className="memory-quote-mark" aria-hidden="true">&ldquo;</span>
              <p className="memory-text">
                Mum shared the story of the dance where she met Dad &mdash; something none of us had ever heard before.
              </p>
            </article>
          </div>

          <div className="stories-treasured-foot">
            <p className="stories-treasured-line">Every family has stories worth preserving.</p>
            <Link
              to="/shop"
              className="btn btn-urgent stories-treasured-cta"
              data-testid="stories-treasured-cta"
            >
              Start Capturing Their Stories
            </Link>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="who-section section" data-testid="who-section">
        <div className="container text-center">
          <h2>Who This Is For</h2>
          <p className="who-description">
            For anyone who doesn&apos;t want to lose the stories of someone they love — especially when they&apos;re not the type to write them down.
          </p>
        </div>
      </section>

      {/* Why Binding Thread - With thread decoration */}
      <section className="why-section section" data-testid="why-section">
        <div className="container">
          <div className="why-grid">
            <div className="why-content">
              <div className="why-header">
                <h2 className="why-title">Why Binding Thread?</h2>
              </div>
              <div className="thread-decoration"></div>
              <p className="why-intro">
                The stories people carry aren&apos;t written down. They&apos;re told in passing, remembered in moments — and often lost over time.
              </p>
              <p className="why-highlight">
                That&apos;s why Binding Thread exists.
              </p>
              <p className="why-body">
                We make it simple for your loved one to share their stories, and turn those moments into <strong>beautifully crafted keepsake letters</strong>. No screens. No apps. No overwhelm.
              </p>
              <p className="why-body">
                Just their voice, captured on paper that will outlive all of us.
              </p>
              <ul className="why-list">
                <li>
                  <CheckCircle size={20} className="check-icon" />
                  <span>No technology needed — just a phone call</span>
                </li>
                <li>
                  <CheckCircle size={20} className="check-icon" />
                  <span>Beautifully crafted keepsake letters delivered as your loved one shares their stories</span>
                </li>
                <li>
                  <CheckCircle size={20} className="check-icon" />
                  <span>Archival quality paper made to last generations</span>
                </li>
                <li>
                  <CheckCircle size={20} className="check-icon" />
                  <span>A meaningful gift for families who want to preserve their story</span>
                </li>
              </ul>
            </div>

            <div className="dont-wait-content">
              <div className="dont-wait-card">
                <h3>Don&apos;t Wait Until the Memories Are Gone</h3>
                <p>
                  Every family reaches a moment when the stories they thought they&apos;d always remember begin to fade. You can start preserving them now, simply and gently.
                </p>
                <Link to="/shop" className="btn btn-urgent btn-full" data-testid="cta-dont-wait">
                  Start Capturing Their Stories
                </Link>
              </div>
              <p className="dont-wait-closing" data-testid="dont-wait-closing">
                You&apos;re trying to hold onto the moments that matter. We&apos;re here to help you keep them safe. <Link to="/about" className="inline-link">Read our story &rarr;</Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Free Guide Opt-in */}
      <section className="optin-section" data-testid="optin-section">
        <div className="container">
          <div className="optin-card">
            <div className="optin-content">
              <span className="optin-badge">FREE GUIDE</span>
              <h2 className="optin-title">10 Questions to Ask Your Mum</h2>
              <p className="optin-subtitle">Before it&apos;s too late&hellip;</p>
              <p className="optin-body">
                Most of us think we know our mum&apos;s story. But the details — the moments, the decisions, the things she never wrote down — are often the ones we miss. This free guide gives you 10 simple prompts to help her share those stories, in her own words. If writing things down isn&apos;t her style, there are simpler ways to capture these stories — even just by phone.
              </p>
              {optinStatus === 'success' ? (
                <div className="optin-success" data-testid="optin-success">
                  <CheckCircle size={22} />
                  <div>
                    <p className="optin-success-title">Your guide is downloading now!</p>
                    <a href="/downloads/10-questions-guide.pdf" download="10-Questions-to-Ask-Your-Mum-Binding-Thread.pdf" className="optin-redownload">
                      <Download size={14} /> Click here if it didn&apos;t start
                    </a>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleOptin} className="optin-form" data-testid="optin-form">
                  <input
                    type="text"
                    placeholder="Your first name"
                    value={optinName}
                    onChange={(e) => setOptinName(e.target.value)}
                    required
                    className="optin-input"
                    data-testid="optin-name-input"
                  />
                  <input
                    type="email"
                    placeholder="Enter your email address"
                    value={optinEmail}
                    onChange={(e) => setOptinEmail(e.target.value)}
                    required
                    className="optin-input"
                    data-testid="optin-email-input"
                  />
                  <button type="submit" className="btn btn-urgent optin-btn" disabled={optinLoading} data-testid="optin-submit-btn">
                    {optinLoading ? 'Sending...' : 'Send Me the Free Guide'}
                  </button>
                  <p className="optin-privacy">No spam, ever. Unsubscribe anytime.</p>
                  <p className="optin-privacy">We&apos;ll also send occasional ideas on how to preserve the stories of someone you love.</p>
                </form>
              )}
            </div>
            <div className="optin-visual">
              <div className="optin-pdf-preview">
                <span className="optin-pdf-label">PDF GUIDE</span>
                <span className="optin-pdf-title">10 Questions<br/>to Ask<br/>Your Mum</span>
                <span className="optin-pdf-brand">BINDING THREAD</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Collection Pricing Section — full-width, deep-sage with gold accents (matches Legacy Collection) */}
      <section className="home-collections" data-testid="pricing-section" aria-labelledby="home-collections-heading">
        <div className="home-collections-inner">
          <div className="home-collections-head">
            <span className="hc-tag">Our Collections</span>
            <div className="hc-gline" aria-hidden="true"></div>
            <h2 id="home-collections-heading" className="hc-headline">Choose your collection</h2>
            <p className="hc-intro">
              Three carefully crafted keepsake letter collections. Our Binding Thread keepsake tin and shipping are included in every package.
            </p>
            <p className="hc-trust" data-testid="home-trust-strip">
              Trusted by families across Australia &amp; the United States
            </p>
          </div>

          <div className="hc-tiers" role="list">
            <article className="hc-tier" role="listitem" data-testid="home-tier-thread">
              <img src="/legacy/keepsake-tin.png" alt="Binding Thread keepsake tin" className="hc-tier-tin" loading="lazy" />
              <p className="hc-tier-name">A Thread</p>
              <p className="hc-tier-price">
                {currency === 'USD' ? '$99' : '$149'}
                <span> {currency === 'USD' ? 'USD' : 'AUD'}</span>
              </p>
              <div className="hc-tier-divider"></div>
              <p className="hc-tier-desc">
                Three beautifully crafted keepsake letters capturing the memories that matter most. The perfect place to begin a story that lasts.
              </p>
              <p className="hc-tier-detail">3 letters · Keepsake tin &amp; shipping included</p>
              <Link
                to="/shop"
                className="hc-btn"
                data-testid="home-tier-thread-cta"
              >
                Begin with A Thread
              </Link>
            </article>

            <article className="hc-tier hc-tier-featured" role="listitem" data-testid="home-tier-woven">
              <span className="hc-tier-badge">Most Loved</span>
              <img src="/legacy/keepsake-tin.png" alt="Binding Thread keepsake tin" className="hc-tier-tin" loading="lazy" />
              <p className="hc-tier-name">Woven</p>
              <p className="hc-tier-price">
                {currency === 'USD' ? '$169' : '$249'}
                <span> {currency === 'USD' ? 'USD' : 'AUD'}</span>
              </p>
              <div className="hc-tier-divider"></div>
              <p className="hc-tier-desc">
                Six letters, each one a piece of a life — a collection that builds into something your family will treasure for generations.
              </p>
              <p className="hc-tier-detail">6 letters · Keepsake tin &amp; shipping included</p>
              <Link
                to="/shop"
                className="hc-btn"
                data-testid="home-tier-woven-cta"
              >
                Begin Woven
              </Link>
            </article>

            <article className="hc-tier" role="listitem" data-testid="home-tier-tapestry">
              <img src="/legacy/keepsake-tin.png" alt="Binding Thread keepsake tin" className="hc-tier-tin" loading="lazy" />
              <p className="hc-tier-name">Tapestry</p>
              <p className="hc-tier-price">
                {currency === 'USD' ? '$289' : '$429'}
                <span> {currency === 'USD' ? 'USD' : 'AUD'}</span>
              </p>
              <div className="hc-tier-divider"></div>
              <p className="hc-tier-desc">
                Twelve letters. Twelve memories. A complete collection — for a life that deserves to be fully told.
              </p>
              <p className="hc-tier-detail">12 letters · Keepsake tin &amp; shipping included</p>
              <Link
                to="/shop"
                className="hc-btn"
                data-testid="home-tier-tapestry-cta"
              >
                Begin Tapestry
              </Link>
            </article>
          </div>
        </div>
      </section>

      {/* Zero-Tech Guarantee - At Bottom */}
      <section id="zero-tech" className="zero-tech section" data-testid="zero-tech">
        <div className="container">
          <div className="zero-tech-content text-center">
            <div className="zero-tech-icon">
              <Shield size={36} />
            </div>
            <h2>No Technology Needed. Ever.</h2>
            <p className="zero-tech-intro">
              Your loved one doesn&apos;t need a computer, smartphone, or internet. Just a phone — and stories to share.
            </p>
            
            <div className="guarantee-grid">
              <div className="guarantee-item">
                <Phone size={22} />
                <h3>Phone Only</h3>
                <p>Call our free number from any phone, anytime — day or night.</p>
              </div>
              
              <div className="guarantee-item">
                <Heart size={22} />
                <h3>Record your Memory</h3>
                <p>Your loved one shares their story in their own words — then simply hangs up.</p>
              </div>
              
              <div className="guarantee-item">
                <Shield size={22} />
                <h3>No Pressure</h3>
                <p>No time limit — they can share as much or as little as they like, no rush, no pressure.</p>
              </div>
            </div>
            <p className="guarantee-footnote">They can call as often as they like — each memory, one call at a time.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
